import React, { useEffect, useState, useMemo } from 'react'
import Cookies from 'js-cookie'
import axios from 'axios'
import Select from 'react-select'
import Topbar from '../../layouts/Topbar'
import Sidebar from '../../layouts/Sidebar'
import { Link } from 'react-router-dom'
import { API_BASE_URL } from '../../../config/api'
import Swal from 'sweetalert2'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

const DepartmentEmployees = () => {
  const baseUrl = API_BASE_URL
  const [employees, setEmployees] = useState([])
  const [departments, setDepartments] = useState([])
  const [costCenters, setCostCenters] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [selectedDepartment, setSelectedDepartment] = useState(null)
  const [selectedCostCenter, setSelectedCostCenter] = useState(null)
  const [page, setPage] = useState(1)
  const [rowsPerPage] = useState(20)
  const [deletingEmployeeId, setDeletingEmployeeId] = useState(null)

  const fetchAll = async () => {
    setLoading(true)
    try {
      const token = Cookies.get('token')
      const [empRes, depRes, ccRes] = await Promise.all([
        axios.get(`${baseUrl}/api/employees/`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${baseUrl}/api/departments/`, { headers: { 'Authorization': `Bearer ${token}` } }),
        axios.get(`${baseUrl}/api/course-centers/`, { headers: { 'Authorization': `Bearer ${token}` } })
      ])
      setEmployees(Array.isArray(empRes.data) ? empRes.data : [])
      setDepartments(Array.isArray(depRes.data) ? depRes.data : [])
      setCostCenters(Array.isArray(ccRes.data) ? ccRes.data : [])
    } catch (err) {
      console.error('Failed to fetch employees:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const departmentOptions = useMemo(() => departments.map(dep => ({
    value: dep.id,
    label: dep.course_center_name ? `${dep.name} (${dep.course_center_name})` : dep.name
  })), [departments])

  const costCenterOptions = useMemo(() => costCenters.map(cc => ({
    value: cc.id,
    label: cc.name
  })), [costCenters])

  const filteredEmployees = useMemo(() => {
    let filtered = employees

    if (search) {
      const s = search.toLowerCase()
      filtered = filtered.filter(emp =>
        emp.first_name?.toLowerCase().includes(s) ||
        emp.last_name?.toLowerCase().includes(s) ||
        emp.national_id?.toLowerCase().includes(s) ||
        emp.position?.toLowerCase().includes(s)
      )
    }
    if (selectedDepartment) {
      filtered = filtered.filter(emp => emp.department === selectedDepartment.value)
    }
    if (selectedCostCenter) {
      const depIds = departments.filter(dep => dep.course_center === selectedCostCenter.value).map(dep => dep.id)
      filtered = filtered.filter(emp => depIds.includes(emp.department))
    }
    return filtered
  }, [employees, search, selectedDepartment, selectedCostCenter, departments])

  const totalPages = Math.ceil(filteredEmployees.length / rowsPerPage)
  const paginatedEmployees = filteredEmployees.slice((page - 1) * rowsPerPage, page * rowsPerPage)

  const handleSearchChange = e => {
    setSearch(e.target.value)
    setPage(1)
  }
  const handleDepartmentChange = val => {
    setSelectedDepartment(val)
    setPage(1)
  }
  const handleCostCenterChange = val => {
    setSelectedCostCenter(val)
    setPage(1)
  }
  const handlePrevPage = () => setPage(p => Math.max(1, p - 1))
  const handleNextPage = () => setPage(p => Math.min(totalPages, p + 1))

  const handleDeleteEmployee = async (employeeId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete this employee. This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    })

    if (!result.isConfirmed) return

    setDeletingEmployeeId(employeeId)
    try {
      const token = Cookies.get('token')
      const response = await axios.delete(`${baseUrl}/api/employees/${employeeId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.status !== 200 && response.status !== 204) {
        throw new Error(`Unexpected response status: ${response.status}`)
      }

      await fetchAll()
      Swal.fire('Deleted!', 'The employee has been deleted.', 'success')
    } catch (err) {
      const message =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to delete employee'
      Swal.fire('Error', message, 'error')
      await fetchAll()
    } finally {
      setDeletingEmployeeId(null)
    }
  }

  const handleExportDepartmentEmployeesPdf = () => {
    if (!filteredEmployees.length) {
      Swal.fire('No data', 'No employees available to export.', 'info')
      return
    }

    const doc = new jsPDF('p', 'mm', 'a4')
    doc.setFontSize(16)
    doc.text('Department Employees Report', 14, 15)
    doc.setFontSize(10)
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 22)

    const tableRows = filteredEmployees.map((emp, index) => {
      const department = departments.find(dep => dep.id === emp.department)
      const costCenter = department
        ? costCenters.find(cc => cc.id === department.course_center)
        : null

      return [
        index + 1,
        emp.first_name || 'N/A',
        emp.last_name || 'N/A',
        emp.national_id || 'N/A',
        emp.position || 'N/A',
        department?.name || 'N/A',
        costCenter?.name || 'N/A',
        `${(emp.performance_progress || 0).toFixed(2)}%`
      ]
    })

    autoTable(doc, {
      head: [['#', 'Name', 'Surname', 'National ID', 'Position', 'Department', 'Cost Center', 'Progress']],
      body: tableRows,
      startY: 28,
      styles: { fontSize: 8.5, cellPadding: 2.5 },
      headStyles: { fillColor: [30, 64, 175] }
    })

    doc.save(`department-employees-${new Date().toISOString().slice(0, 10)}.pdf`)
    Swal.fire('Success', 'Department employees PDF generated.', 'success')
  }

  return (
    <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
      <Topbar />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content pb-0">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
            <div>
              <h4 className="fw-bold mb-1">Employees</h4>
              <p className="text-muted mb-0 fs-13">View and manage all employees in your department</p>
            </div>
            <button
              type="button"
              onClick={handleExportDepartmentEmployeesPdf}
              className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
              disabled={loading || filteredEmployees.length === 0}
              title="Export department employees as PDF"
            >
              <i className="ti ti-file-type-pdf fs-16" />
              <span>Export PDF</span>
            </button>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="card-body">
              <div className="row g-3 align-items-center">
                <div className="col-md-4">
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control ps-5"
                      placeholder="Search by name, national ID, or position..."
                      value={search}
                      onChange={handleSearchChange}
                      disabled={loading}
                    />
                    <i className="ti ti-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-16" />
                  </div>
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-medium fs-13 mb-1">Department</label>
                  <Select
                    value={selectedDepartment}
                    onChange={handleDepartmentChange}
                    options={departmentOptions}
                    isClearable
                    placeholder="All Departments"
                    isDisabled={loading}
                    styles={{
                      control: (base) => ({ ...base, minHeight: '38px' }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 })
                    }}
                    menuPortalTarget={document.body}
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label fw-medium fs-13 mb-1">Cost Center</label>
                  <Select
                    value={selectedCostCenter}
                    onChange={handleCostCenterChange}
                    options={costCenterOptions}
                    isClearable
                    placeholder="All Cost Centers"
                    isDisabled={loading}
                    styles={{
                      control: (base) => ({ ...base, minHeight: '38px' }),
                      menuPortal: (base) => ({ ...base, zIndex: 9999 })
                    }}
                    menuPortalTarget={document.body}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mb-4">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">Name</th>
                    <th>Surname</th>
                    <th>National ID</th>
                    <th>Position</th>
                    <th style={{ width: '220px' }}>Progress</th>
                    <th className="text-end pe-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="text-muted fs-13 mt-2 mb-0">Loading employees...</p>
                      </td>
                    </tr>
                  ) : paginatedEmployees.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-5 text-muted">
                        <i className="ti ti-users-minus fs-36 d-block mb-2 text-secondary opacity-50" />
                        No employees found.
                      </td>
                    </tr>
                  ) : (
                    paginatedEmployees.map(emp => {
                      const progressVal = Number(emp.performance_progress || 0)
                      const barBg = emp.performance_indicator || '#4F46E5'
                      return (
                        <tr key={emp.id}>
                          <td className="ps-3 fw-semibold">{emp.first_name || '—'}</td>
                          <td>{emp.last_name || '—'}</td>
                          <td>{emp.national_id || '—'}</td>
                          <td>
                            <span className="badge bg-light text-dark border">
                              {emp.position || '—'}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress flex-grow-1" style={{ height: '18px', backgroundColor: '#eef2ff' }}>
                                <div
                                  className="progress-bar progress-bar-striped progress-bar-animated"
                                  role="progressbar"
                                  style={{
                                    width: `${progressVal.toFixed(2)}%`,
                                    backgroundColor: barBg
                                  }}
                                  aria-valuenow={progressVal}
                                  aria-valuemin="0"
                                  aria-valuemax="100"
                                >
                                  <span className="fs-11 fw-bold">{progressVal.toFixed(1)}%</span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="text-end pe-3">
                            <div className="d-flex align-items-center justify-content-end gap-1">
                              <Link
                                to={`/hod/employee/${emp.id}/goals`}
                                state={{ employeeId: emp.id }}
                                className="btn btn-sm btn-icon btn-light"
                                title="View Goals"
                              >
                                <i className="ti ti-eye text-primary" />
                              </Link>
                              <Link
                                to={`/hod/update-employee/${emp.id}`}
                                state={{ employeeId: emp.id }}
                                className="btn btn-sm btn-icon btn-light"
                                title="Edit Employee"
                              >
                                <i className="ti ti-edit text-success" />
                              </Link>
                              <button
                                className="btn btn-sm btn-icon btn-light"
                                title="Delete"
                                onClick={() => handleDeleteEmployee(emp.id)}
                                disabled={deletingEmployeeId === emp.id}
                              >
                                <i className="ti ti-trash text-danger" />
                              </button>
                              <Link
                                to={`/hod/employee-goals/${emp.id}/reviews`}
                                state={{ employeeId: emp.id }}
                                className="btn btn-sm btn-outline-primary ms-1"
                                title="Review KPIs"
                              >
                                Reviews
                              </Link>
                              <Link
                                to={`/hod/employee/${emp.id}/report`}
                                state={{ employeeId: emp.id }}
                                className="btn btn-sm btn-outline-info ms-1"
                                title="View Report"
                              >
                                Report
                              </Link>
                            </div>
                          </td>
                        </tr>
                      )
                    })
                  )}
                </tbody>
              </table>
            </div>

            {!loading && filteredEmployees.length > 0 && (
              <div className="card-footer d-flex align-items-center justify-content-between py-2 px-3 border-top">
                <span className="text-muted fs-13">
                  Showing {Math.min((page - 1) * rowsPerPage + 1, filteredEmployees.length)} to {Math.min(page * rowsPerPage, filteredEmployees.length)} of {filteredEmployees.length} employees
                </span>
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePrevPage}
                    disabled={page === 1}
                    className="btn btn-sm btn-light"
                  >
                    <i className="ti ti-chevron-left me-1" />
                    Prev
                  </button>
                  <span className="fs-13 fw-semibold px-2">
                    Page {page} of {totalPages || 1}
                  </span>
                  <button
                    type="button"
                    onClick={handleNextPage}
                    disabled={page === totalPages || totalPages === 0}
                    className="btn btn-sm btn-light"
                  >
                    Next
                    <i className="ti ti-chevron-right ms-1" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="footer d-flex align-items-center justify-content-between flex-column flex-sm-row row-gap-2 border-top py-2 px-3">
          <p className="text-dark mb-0">
            2026 © <span className="link-primary">Casoe Technologies</span>, All Rights Reserved
          </p>
        </div>
      </div>
    </div>
  )
}

export default DepartmentEmployees